import { useState } from "react";
import { toast } from "sonner";
import { Phone, MapPin, Navigation, Clock, Package, Wrench, Check, Camera } from "lucide-react";
import { api, errMsg, fmtWindow, mapsLink, FLOW, STATUS } from "../lib/api";
import { Btn, Card, ServiceIcon, StatusChip, Money } from "./kit";

export function Stepper({ status }) {
  const idx = FLOW.indexOf(status);
  return (
    <div className="flex gap-1" data-testid="job-stepper">
      {FLOW.map((s, i) => (
        <div key={s} className="flex-1" title={STATUS[s]}>
          <div className={`h-1.5 rounded-full ${i < idx ? "bg-[#FF6B00]/60" : i === idx ? "bg-[#FF6B00]" : "bg-[#262626]"}`} />
        </div>
      ))}
    </div>
  );
}

export function JobSummary({ job, payout }) {
  return (
    <Card className="space-y-3" data-testid="job-summary">
      <div className="flex items-start gap-3">
        <ServiceIcon k={job.service_key} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="font-head text-2xl font-semibold uppercase leading-tight" data-testid="job-service-name">{job.service_name}</div>
            <StatusChip status={job.status} needs={job.dispatch?.needs_dispatch && job.status === "open"} testid="job-status-chip" />
          </div>
          <div className="text-sm text-neutral-400">#{job.number} · {job.vehicle_label} {job.vehicle?.engine && `· ${job.vehicle.engine}`}</div>
        </div>
      </div>
      {job.status !== "draft" && job.status !== "open" && job.status !== "cancelled" && <Stepper status={job.status} />}
      <div className="grid grid-cols-1 gap-2 text-sm">
        <div className="flex gap-2 text-neutral-300"><Clock className="w-4 h-4 text-neutral-500 mt-0.5" />{fmtWindow(job.window_start, job.window_end)}</div>
        <div className="flex gap-2 text-neutral-300"><Package className="w-4 h-4 text-neutral-500 mt-0.5" />Parts: {job.parts_responsibility === "driven" ? "Driven supplies" : job.parts_responsibility === "customer" ? "Customer-supplied (approved)" : "None needed"}{job.parts_ready && ` · ${job.parts_ready}`}</div>
        {(job.required_skills || job.required_tools) && (
          <div className="flex gap-2 text-neutral-300"><Wrench className="w-4 h-4 text-neutral-500 mt-0.5" />{[job.required_skills, job.required_tools].filter(Boolean).join(" · ")}</div>
        )}
      </div>
      {job.description && <p className="text-sm text-neutral-400 border-t border-[#1f1f1f] pt-3">{job.description}</p>}
      {payout != null && (
        <div className="flex justify-between items-center border-t border-[#1f1f1f] pt-3">
          <span className="text-xs uppercase tracking-wider text-neutral-500">Your payout</span>
          <Money v={payout} green className="text-2xl font-semibold" testid="job-payout" />
        </div>
      )}
    </Card>
  );
}

export function CustomerCard({ job }) {
  const c = job.customer;
  return (
    <Card className="space-y-2" data-testid="customer-card">
      <div className="text-xs uppercase tracking-wider text-neutral-500">Customer (assigned only)</div>
      <div className="font-medium" data-testid="customer-name">{c.name}</div>
      <div className="text-sm text-neutral-300 flex gap-2" data-testid="customer-address"><MapPin className="w-4 h-4 text-neutral-500 mt-0.5" />{[c.address, c.city, c.state || "OH", c.zip].filter(Boolean).join(", ")}</div>
      <div className="grid grid-cols-2 gap-2 pt-1">
        <a href={`tel:${c.phone}`} className="h-11 rounded-xl border border-[#333] flex items-center justify-center gap-2 text-sm" data-testid="customer-call-link"><Phone className="w-4 h-4" />{c.phone || "No phone"}</a>
        <a href={mapsLink(c)} target="_blank" rel="noreferrer" className="h-11 rounded-xl border border-[#333] flex items-center justify-center gap-2 text-sm" data-testid="open-in-maps-link"><Navigation className="w-4 h-4" />Open in Maps</a>
      </div>
    </Card>
  );
}

export function PhotoJumpButton({ job, onJump }) {
  const after = ["working", "after_check", "awaiting_review", "completed"].includes(job.status);
  return (
    <Btn variant="outline" size="lg" className="w-full" onClick={() => onJump({ tab: after ? "after" : "before", n: Date.now() })} data-testid="take-photos-btn">
      <Camera className="w-5 h-5 text-[#FF6B00]" />Take photos · {after ? "After work" : "Before work"}
    </Btn>
  );
}

const NEXT = {
  assigned: ["en_route", "Start driving · En route"], en_route: ["before_check", "Arrived · Before-work check"],
  before_check: ["working", "Start work"], working: ["after_check", "Work done · After-work check"],
  after_check: ["awaiting_review", "Submit completion"],
};

export function NextStep({ job, reload }) {
  const [busy, setBusy] = useState(false);
  const n = NEXT[job.status];
  if (!n) return null;
  const blockers = [];
  if (n[0] === "working") {
    if (!job.gates.before_docs) blockers.push("Submit the before-work report");
    if (!job.gates.work_authorized) blockers.push("Record customer approval for the current quote");
  }
  if (n[0] === "awaiting_review") {
    if (!job.gates.after_docs) blockers.push("Submit the after-work report");
    if (!job.gates.work_authorized) blockers.push("Revised quote needs customer approval");
  }
  const go = async () => {
    setBusy(true);
    try {
      await api.post(`/jobs/${job.id}/status`, { to: n[0] });
      toast.success(STATUS[n[0]]);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-2" data-testid="next-step">
      {blockers.map((b) => <div key={b} className="text-xs text-amber-400">• {b}</div>)}
      <Btn size="lg" className="w-full" onClick={go} loading={busy} disabled={blockers.length > 0} data-testid="next-step-btn"><Check className="w-5 h-5" />{n[1]}</Btn>
    </div>
  );
}
